import type * as React from "react";

import { cn } from "../lib/cn";

export interface CardProps extends React.ComponentProps<"div"> {
  /** Dense dashboards: 16px padding instead of 24px. */
  compact?: boolean;
}

/** The default container for one topic. Don't nest cards. */
export function Card({ className, compact, ...props }: CardProps) {
  return (
    <div
      data-slot="card"
      data-compact={compact ? "" : undefined}
      className={cn(
        "group/card flex min-w-0 flex-col rounded-lg border border-border bg-card text-card-foreground shadow-xs",
        className,
      )}
      {...props}
    />
  );
}

export interface CardHeaderProps extends Omit<React.ComponentProps<"div">, "title"> {
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** Usually a ghost icon button or a small outline button. */
  action?: React.ReactNode;
  /** Heading level for the title (default h3). */
  as?: "h2" | "h3" | "h4";
}

export function CardHeader({
  className,
  title,
  description,
  action,
  as: Heading = "h3",
  children,
  ...props
}: CardHeaderProps) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "flex items-start justify-between gap-3 px-6 pt-6 group-data-[compact]/card:px-4 group-data-[compact]/card:pt-4",
        className,
      )}
      {...props}
    >
      <div className="min-w-0">
        {title ? (
          <Heading className="m-0 text-base leading-6 font-semibold tracking-[-0.01em]">{title}</Heading>
        ) : null}
        {description ? (
          <p className="mt-0.5 text-[13px] leading-[18px] text-muted-foreground">{description}</p>
        ) : null}
        {children}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

export function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("p-6 group-data-[compact]/card:p-4", className)}
      {...props}
    />
  );
}

export function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn(
        "flex items-center gap-2 px-6 pb-6 group-data-[compact]/card:px-4 group-data-[compact]/card:pb-4",
        className,
      )}
      {...props}
    />
  );
}
